import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  Plus,
  Trash2,
  Edit2,
  ChevronDown,
  GripVertical,
  SortAsc,
  Crosshair,
  Bell,
  BellOff,
  Info,
  Copy,
} from "lucide-react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DraggableAttributes,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { logActivity } from "../lib/activity-logger";
import { Modal, useToast } from "./ui/ToastModal";
import { supabase, requireOrganizationId } from "../lib/supabase";
import { DEFAULT_SUBCATEGORIES } from "../lib/calculations";
import type {
  ClaimsCategory,
  ClaimsSubcategory,
} from "../types/database.types";
import { logError } from "../lib/logError";

interface CategoryWithSubs extends ClaimsCategory {
  subcategories: ClaimsSubcategory[];
}

interface SubcategoryTarget {
  id?: string;
  subcategory_id: string;
  target_type: "percentage" | "amount";
  target_value: number;
  trigger_mode: "once" | "every_change";
  message: string;
  is_enabled: boolean;
}

interface CategoryTarget {
  id?: string;
  category_id: string;
  target_type: "percentage" | "amount";
  target_value: number;
  trigger_mode: "once" | "every_change";
  message: string;
  is_enabled: boolean;
  included_subcategory_ids: string[];
}

interface EditCategoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  categories: CategoryWithSubs[];
  onCategoriesUpdate: () => void;
}

// Wrapper component for sortable category - needed to use hooks properly
interface SortableCategoryItemProps {
  id: string;
  children: (props: { attributes: DraggableAttributes; listeners: ReturnType<typeof useSortable>['listeners'] }) => React.ReactNode;
}

function SortableCategoryItem({ id, children }: SortableCategoryItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    border: "1px solid var(--border-color)",
    borderRadius: "var(--border-radius-md)",
    overflow: "hidden" as const,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ attributes, listeners })}
    </div>
  );
}

// Sortable subcategory wrapper
interface SortableSubItemProps {
  id: string;
  children: (props: { attributes: DraggableAttributes; listeners: ReturnType<typeof useSortable>['listeners'] }) => React.ReactNode;
}

function SortableSubItem({ id, children }: SortableSubItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      {children({ attributes, listeners })}
    </div>
  );
}

export function EditCategoriesModal({
  isOpen,
  onClose,
  projectId,
  categories,
  onCategoriesUpdate,
}: EditCategoriesModalProps) {
  const { addToast } = useToast();
  const [saving, setSaving] = useState(false);
  const [localCategories, setLocalCategories] = useState<CategoryWithSubs[]>(
    [],
  );
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [showSubDropdown, setShowSubDropdown] = useState<string | null>(null);
  const [targets, setTargets] = useState<Map<string, SubcategoryTarget>>(new Map());
  const [expandedTarget, setExpandedTarget] = useState<string | null>(null);
  const [hoveredTargetBadge, setHoveredTargetBadge] = useState<{ id: string; rect: DOMRect } | null>(null);
  const [categoryTargets, setCategoryTargets] = useState<Map<string, CategoryTarget>>(new Map());
  const [expandedCategoryTarget, setExpandedCategoryTarget] = useState<string | null>(null);

  // Refs for auto-focus and scroll
  const categoryInputRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const subcategoryInputRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);

  // DnD Sensors
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Handle drag end for both categories and subcategories
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setLocalCategories((prev) => {
      // 1. Try to find if active ID is a category
      const activeCatIndex = prev.findIndex((c) => c.id === active.id);
      const overCatIndex = prev.findIndex((c) => c.id === over.id);

      if (activeCatIndex !== -1 && overCatIndex !== -1) {
        // Reordering categories
        return arrayMove(prev, activeCatIndex, overCatIndex);
      }

      // 2. Try to find if active ID is a subcategory
      // We need to find the parent category for the active item
      const parentCatIndex = prev.findIndex((c) =>
        c.subcategories.some((s) => s.id === active.id),
      );

      if (parentCatIndex !== -1) {
        const parentCat = prev[parentCatIndex];
        // Ensure 'over' is also in the same subcategory list
        const overSubIndex = parentCat.subcategories.findIndex(
          (s) => s.id === over.id,
        );
        const activeSubIndex = parentCat.subcategories.findIndex(
          (s) => s.id === active.id,
        );

        if (overSubIndex !== -1 && activeSubIndex !== -1) {
          // Reordering subcategories within the same category
          const newSubcategories = arrayMove(
            parentCat.subcategories,
            activeSubIndex,
            overSubIndex,
          );

          const newCategories = [...prev];
          newCategories[parentCatIndex] = {
            ...parentCat,
            subcategories: newSubcategories,
          };
          return newCategories;
        }
      }

      return prev;
    });
  };

  // Initialize local state when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalCategories(JSON.parse(JSON.stringify(categories)));
      setExpandedCategory(null);
      setShowSubDropdown(null);
      setExpandedTarget(null);
      // Load existing targets
      loadTargets();
      loadCategoryTargets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadTargets and loadCategoryTargets depend only on categories (already included)
  }, [isOpen, categories]);

  // Load existing targets from DB
  const loadTargets = async () => {
    const allSubIds = categories.flatMap((c) =>
      c.subcategories.map((s) => s.id)
    ).filter((id) => !id.startsWith("new-"));

    if (allSubIds.length === 0) return;

    const { data, error } = await supabase
      .from("claims_subcategory_targets")
      .select("*")
      .in("subcategory_id", allSubIds);

    if (error) {
      logError("Error loading targets:", error);
      return;
    }

    const map = new Map<string, SubcategoryTarget>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Supabase dynamic table query result
    data?.forEach((t: any) => {
      map.set(t.subcategory_id, {
        id: t.id,
        subcategory_id: t.subcategory_id,
        target_type: t.target_type,
        target_value: t.target_value,
        trigger_mode: t.trigger_mode,
        message: t.message || "",
        is_enabled: t.is_enabled,
      });
    });
    setTargets(map);
  };

  // Load existing category targets from DB
  const loadCategoryTargets = async () => {
    const catIds = categories.map((c) => c.id).filter((id) => !id.startsWith("new-"));
    if (catIds.length === 0) return;

    const { data, error } = await supabase
      .from("claims_category_targets")
      .select("*")
      .in("category_id", catIds);

    if (error) {
      logError("Error loading category targets:", error);
      return;
    }

    const map = new Map<string, CategoryTarget>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Supabase dynamic table query result
    data?.forEach((t: any) => {
      map.set(t.category_id, {
        id: t.id,
        category_id: t.category_id,
        target_type: t.target_type,
        target_value: t.target_value,
        trigger_mode: t.trigger_mode,
        message: t.message || "",
        is_enabled: t.is_enabled,
        included_subcategory_ids: t.included_subcategory_ids || [],
      });
    });
    setCategoryTargets(map);
  };

  // Update a target in local state
  const updateTarget = (subId: string, field: keyof SubcategoryTarget, value: SubcategoryTarget[keyof SubcategoryTarget]) => {
    setTargets((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(subId) || {
        subcategory_id: subId,
        target_type: "percentage" as const,
        target_value: 75,
        trigger_mode: "once" as const,
        message: "",
        is_enabled: true,
      };
      newMap.set(subId, { ...existing, [field]: value });
      return newMap;
    });
  };

  // Toggle target enabled/disabled
  const toggleTarget = (subId: string) => {
    const existing = targets.get(subId);
    if (existing) {
      updateTarget(subId, "is_enabled", !existing.is_enabled);
    } else {
      // Create new target with defaults
      setTargets((prev) => {
        const newMap = new Map(prev);
        newMap.set(subId, {
          subcategory_id: subId,
          target_type: "percentage",
          target_value: 75,
          trigger_mode: "once",
          message: "",
          is_enabled: true,
        });
        return newMap;
      });
      setExpandedTarget(subId);
    }
  };

  // Save targets to DB (called during handleSave)
  const saveTargets = async () => {
    for (const [subId, target] of targets.entries()) {
      // Skip targets referencing new subcategories (they'll be handled after insert)
      if (subId.startsWith("new-sub-")) continue;

      if (target.id) {
        // Update existing target
        const { error } = await supabase
          .from("claims_subcategory_targets")
          .update({
            target_type: target.target_type,
            target_value: target.target_value,
            trigger_mode: target.trigger_mode,
            message: target.message,
            is_enabled: target.is_enabled,
          })
          .eq("id", target.id);

        if (error) logError("Error updating target:", error);
      } else {
        // Insert new target
        const { error } = await supabase
          .from("claims_subcategory_targets")
          .insert({
            subcategory_id: subId,
            target_type: target.target_type,
            target_value: target.target_value,
            trigger_mode: target.trigger_mode,
            message: target.message,
            is_enabled: target.is_enabled,
          });

        if (error) logError("Error inserting target:", error);
      }
    }

    // Save category targets
    for (const [catId, target] of categoryTargets.entries()) {
      if (catId.startsWith("new-")) continue;

      if (target.id) {
        const { error } = await supabase
          .from("claims_category_targets")
          .update({
            target_type: target.target_type,
            target_value: target.target_value,
            trigger_mode: target.trigger_mode,
            message: target.message,
            is_enabled: target.is_enabled,
            included_subcategory_ids: target.included_subcategory_ids,
            updated_at: new Date().toISOString(),
          })
          .eq("id", target.id);
        if (error) logError("Error updating category target:", error);
      } else {
        const { error } = await supabase
          .from("claims_category_targets")
          .insert({
            category_id: catId,
            target_type: target.target_type,
            target_value: target.target_value,
            trigger_mode: target.trigger_mode,
            message: target.message,
            is_enabled: target.is_enabled,
            included_subcategory_ids: target.included_subcategory_ids,
          });
        if (error) logError("Error inserting category target:", error);
      }
    }
  };

  // Update a category target in local state
  const updateCategoryTarget = (catId: string, field: keyof CategoryTarget, value: CategoryTarget[keyof CategoryTarget]) => {
    setCategoryTargets((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(catId) || {
        category_id: catId,
        target_type: "percentage" as const,
        target_value: 80,
        trigger_mode: "once" as const,
        message: "",
        is_enabled: true,
        included_subcategory_ids: [] as string[],
      };
      newMap.set(catId, { ...existing, [field]: value });
      return newMap;
    });
  };

  // Toggle category target
  const toggleCategoryTarget = (catId: string, cat: CategoryWithSubs) => {
    const existing = categoryTargets.get(catId);
    if (existing) {
      updateCategoryTarget(catId, "is_enabled", !existing.is_enabled);
    } else {
      // Create new with all subcategories selected by default
      const allSubIds = cat.subcategories
        .filter((s) => s.name.toUpperCase() !== "FIXED AMOUNT")
        .map((s) => s.id)
        .filter((id) => !id.startsWith("new-sub-"));
      setCategoryTargets((prev) => {
        const newMap = new Map(prev);
        newMap.set(catId, {
          category_id: catId,
          target_type: "percentage",
          target_value: 80,
          trigger_mode: "once",
          message: "",
          is_enabled: true,
          included_subcategory_ids: allSubIds,
        });
        return newMap;
      });
      setExpandedCategoryTarget(catId);
    }
  };

  // Toggle subcategory inclusion in category target
  const toggleSubcategoryInclusion = (catId: string, subId: string) => {
    setCategoryTargets((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(catId);
      if (!existing) return prev;
      const ids = existing.included_subcategory_ids;
      const newIds = ids.includes(subId)
        ? ids.filter((id) => id !== subId)
        : [...ids, subId];
      newMap.set(catId, { ...existing, included_subcategory_ids: newIds });
      return newMap;
    });
  };

  // Select/deselect all subcategories
  const toggleAllSubcategories = (catId: string, cat: CategoryWithSubs, selectAll: boolean) => {
    setCategoryTargets((prev) => {
      const newMap = new Map(prev);
      const existing = newMap.get(catId);
      if (!existing) return prev;
      const allIds = selectAll
        ? cat.subcategories
            .filter((s) => s.name.toUpperCase() !== "FIXED AMOUNT" && !s.id.startsWith("new-sub-"))
            .map((s) => s.id)
        : [];
      newMap.set(catId, { ...existing, included_subcategory_ids: allIds });
      return newMap;
    });
  };

  // Get available predefined subcategories (not already in category)
  const getAvailableSubcategories = (cat: CategoryWithSubs) => {
    const existingNames = new Set(
      cat.subcategories.map((s) => s.name.toLowerCase()),
    );
    return DEFAULT_SUBCATEGORIES.filter(
      (name) => !existingNames.has(name.toLowerCase()),
    );
  };

  // Update category name
  const updateCategoryName = (catId: string, name: string) => {
    setLocalCategories((prev) =>
      prev.map((cat) => (cat.id === catId ? { ...cat, name } : cat)),
    );
  };

  // Update custom amount for fixed amount categories
  const updateCategoryAmount = (catId: string, amount: number) => {
    setLocalCategories((prev) =>
      prev.map((cat) =>
        cat.id === catId ? { ...cat, custom_amount: amount } : cat,
      ),
    );
  };

  // Toggle between fixed amount and subcategories mode (auto-sorts fixed to top)
  const toggleCategoryMode = (catId: string) => {
    setLocalCategories((prev) =>
      prev.map((cat) =>
        cat.id === catId
          ? { ...cat, is_custom_amount_only: !cat.is_custom_amount_only }
          : cat,
      ),
    );
  };

  const createDraftId = (prefix: string) =>
    `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

  // Delete category
  const deleteCategory = (catId: string) => {
    setLocalCategories((prev) => prev.filter((cat) => cat.id !== catId));
  };

  const cloneCategory = (source: CategoryWithSubs) => {
    const newId = createDraftId("new");
    const clonedSubcategories = source.is_custom_amount_only
      ? []
      : source.subcategories.map((sub, index) => ({
          ...sub,
          id: createDraftId("new-sub"),
          category_id: newId,
          is_default: false,
          sort_order: index,
          created_at: null,
        }));

    setLocalCategories((prev) => {
      const sourceIndex = prev.findIndex((cat) => cat.id === source.id);
      const insertIndex = sourceIndex >= 0 ? sourceIndex + 1 : prev.length;
      const clonedCategory: CategoryWithSubs = {
        ...source,
        id: newId,
        name: source.name ? `${source.name} COPY` : "CATEGORY COPY",
        project_id: projectId,
        is_default: false,
        sort_order: insertIndex,
        created_at: null,
        subcategories: clonedSubcategories,
      };
      const next = [...prev];
      next.splice(insertIndex, 0, clonedCategory);
      return next;
    });

    setExpandedCategory(source.is_custom_amount_only ? null : newId);
    setExpandedCategoryTarget(null);
    setShowSubDropdown(null);

    setTimeout(() => {
      const input = categoryInputRefs.current.get(newId);
      if (input) {
        input.focus();
        input.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  };

  // Add new category with auto-focus
  const addCategory = () => {
    const newId = `new-${Date.now()}`;
    const newCategory: CategoryWithSubs = {
      id: newId,
      name: "",
      project_id: projectId,
      is_custom_amount_only: false,
      custom_amount: null,
      is_default: false,
      sort_order: localCategories.length,
      created_at: null,
      subcategories: [],
    };
    setLocalCategories((prev) => [...prev, newCategory]);
    setExpandedCategory(newId);

    // Auto-focus after state update
    setTimeout(() => {
      const input = categoryInputRefs.current.get(newId);
      if (input) {
        input.focus();
        input.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  };

  // Update subcategory
  const updateSubcategory = (
    catId: string,
    subId: string,
    field: "name" | "amount",
    value: string | number,
  ) => {
    setLocalCategories((prev) =>
      prev.map((cat) =>
        cat.id === catId
          ? {
              ...cat,
              subcategories: cat.subcategories.map((sub) =>
                sub.id === subId ? { ...sub, [field]: value } : sub,
              ),
            }
          : cat,
      ),
    );
  };

  // Delete subcategory
  const deleteSubcategory = (catId: string, subId: string) => {
    setLocalCategories((prev) =>
      prev.map((cat) =>
        cat.id === catId
          ? {
              ...cat,
              subcategories: cat.subcategories.filter(
                (sub) => sub.id !== subId,
              ),
            }
          : cat,
      ),
    );
  };

  const cloneSubcategory = (catId: string, source: ClaimsSubcategory) => {
    const newId = createDraftId("new-sub");
    setLocalCategories((prev) =>
      prev.map((cat) =>
        cat.id === catId
          ? (() => {
              const sourceIndex = cat.subcategories.findIndex(
                (sub) => sub.id === source.id,
              );
              const insertIndex =
                sourceIndex >= 0 ? sourceIndex + 1 : cat.subcategories.length;
              const clonedSubcategory: ClaimsSubcategory = {
                ...source,
                id: newId,
                name: source.name ? `${source.name} COPY` : "Subcategory Copy",
                category_id: catId,
                is_default: false,
                sort_order: insertIndex,
                created_at: null,
              };
              const nextSubcategories = [...cat.subcategories];
              nextSubcategories.splice(insertIndex, 0, clonedSubcategory);
              return { ...cat, subcategories: nextSubcategories };
            })()
          : cat,
      ),
    );
    setExpandedCategory(catId);
    setExpandedTarget(null);

    setTimeout(() => {
      const input = subcategoryInputRefs.current.get(newId);
      if (input) {
        input.focus();
        input.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  };

  // Add subcategory (blank or from predefined) - auto-sorts alphabetically
  const addSubcategory = (catId: string, predefinedName?: string) => {
    const cat = localCategories.find((c) => c.id === catId);
    if (!cat) return;

    const newId = `new-sub-${Date.now()}`;
    const newSub: ClaimsSubcategory = {
      id: newId,
      name: predefinedName || "",
      amount: 0,
      category_id: catId,
      is_default: !!predefinedName,
      sort_order: cat.subcategories.length,
      created_at: null,
    };

    setLocalCategories((prev) =>
      prev.map((c) => {
        if (c.id !== catId) return c;
        const newSubs = [...c.subcategories, newSub];
        return {
          ...c,
          subcategories: newSubs,
        };
      }),
    );

    setShowSubDropdown(null);

    // Auto-focus the new subcategory's amount field (name is already set if predefined)
    setTimeout(() => {
      const input = subcategoryInputRefs.current.get(newId);
      if (input) {
        input.focus();
        input.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);
  };

  // Auto Sort All: Fixed Amount Top + Alphabetical
  const autoSortAll = () => {
    setLocalCategories((prev) => {
      const sorted = [...prev].map((cat) => {
        // Sort subcategories alphabetically
        const sortedSubs = [...cat.subcategories].sort((a, b) =>
          a.name.localeCompare(b.name, undefined, {
            numeric: true,
            sensitivity: "base",
          }),
        );
        return { ...cat, subcategories: sortedSubs };
      });

      // Sort categories: Fixed First, Then Alphabetical
      sorted.sort((a, b) => {
        // 1. Fixed Amount First
        if (a.is_custom_amount_only && !b.is_custom_amount_only) return -1;
        if (!a.is_custom_amount_only && b.is_custom_amount_only) return 1;

        // 2. Alphabetical
        return a.name.localeCompare(b.name, undefined, {
          numeric: true,
          sensitivity: "base",
        });
      });

      return sorted;
    });

    logActivity({
      projectId,
      actionType: "UPDATE",
      entityType: "CATEGORY",
      description: "Auto-sorted all categories and subcategories",
    });
  };

  // Save all changes
  const handleSave = async () => {
    setSaving(true);

    try {
      // Validate
      for (const cat of localCategories) {
        if (!cat.name.trim()) {
          addToast({
            type: "error",
            title: "Validation Error",
            message: "All categories must have a name",
          });
          setSaving(false);
          return;
        }

        if (!cat.is_custom_amount_only) {
          for (const sub of cat.subcategories) {
            if (!sub.name.trim()) {
              addToast({
                type: "error",
                title: "Validation Error",
                message: `All subcategories in "${cat.name}" must have a name`,
              });
              setSaving(false);
              return;
            }
          }
        }
      }

      // Find deleted categories
      const originalIds = new Set(categories.map((c) => c.id));
      const localIds = new Set(localCategories.map((c) => c.id));
      const deletedCatIds = [...originalIds].filter(
        (id) => !localIds.has(id) && !id.startsWith("new-"),
      );

      for (const catId of deletedCatIds) {
        // Find name for log
        const catName =
          categories.find((c) => c.id === catId)?.name || "Unknown";
        await supabase.from("claims_categories").delete().eq("id", catId);

        // Log Activity: Delete Category
        await logActivity({
          projectId,
          actionType: "DELETE",
          entityType: "CATEGORY",
          entityId: catId,
          description: `Deleted category "${catName}"`,
        });
      }

      // Process each category
      for (let i = 0; i < localCategories.length; i++) {
        const cat = localCategories[i];
        const isNewCategory = cat.id.startsWith("new-");

        if (isNewCategory) {
          const { data: newCat, error: catError } = await supabase
            .from("claims_categories")
            .insert({
              organization_id: requireOrganizationId(),
              name: cat.name.toUpperCase(),
              project_id: projectId,
              is_custom_amount_only: cat.is_custom_amount_only,
              custom_amount: cat.is_custom_amount_only
                ? Number(cat.custom_amount) || 0
                : null,
              sort_order: i,
            })
            .select()
            .single();

          if (catError) throw catError;

          // Log Activity: Create Category
          await logActivity({
            projectId,
            actionType: "CREATE",
            entityType: "CATEGORY",
            entityId: newCat.id,
            description: `Created category "${newCat.name}"`,
          });

          if (cat.is_custom_amount_only) {
            // Create required default subcategory for fixed amount
            const { error: subError } = await supabase
              .from("claims_subcategories")
              .insert({
                organization_id: requireOrganizationId(),
                name: "FIXED AMOUNT",
                amount: Number(cat.custom_amount) || 0,
                category_id: newCat.id,
                sort_order: 0,
              });
            if (subError) throw subError;
          } else if (cat.subcategories.length > 0) {
            const subsToInsert = cat.subcategories.map((sub, j) => ({
              organization_id: requireOrganizationId(),
              name: sub.name,
              amount: Number(sub.amount) || 0,
              category_id: newCat.id,
              sort_order: j,
            }));

            const { error: subError } = await supabase
              .from("claims_subcategories")
              .insert(subsToInsert);

            if (subError) throw subError;
          }
        } else {
          const { error: catError } = await supabase
            .from("claims_categories")
            .update({
              name: cat.name.toUpperCase(),
              is_custom_amount_only: cat.is_custom_amount_only,
              custom_amount: cat.is_custom_amount_only
                ? Number(cat.custom_amount) || 0
                : null,
              sort_order: i,
            })
            .eq("id", cat.id);

          if (catError) throw catError;

          // Check if name changed for log
          const originalCat = categories.find((c) => c.id === cat.id);
          if (originalCat && originalCat.name !== cat.name.toUpperCase()) {
            await logActivity({
              projectId,
              actionType: "UPDATE",
              entityType: "CATEGORY",
              entityId: cat.id,
              description: `Renamed category from "${
                originalCat.name
              }" to "${cat.name.toUpperCase()}"`,
            });
          }

          if (cat.is_custom_amount_only) {
            // Ensure FIXED AMOUNT subcategory exists and is up to date
            // First check if we already have it in local state
            const fixedSub = cat.subcategories.find(
              (s) => s.name.toUpperCase() === "FIXED AMOUNT",
            );

            if (fixedSub) {
              // Update existing
              await supabase
                .from("claims_subcategories")
                .update({
                  amount: Number(cat.custom_amount) || 0,
                })
                .eq("id", fixedSub.id);
            } else {
              // Create missing
              await supabase.from("claims_subcategories").insert({
                organization_id: requireOrganizationId(),
                name: "FIXED AMOUNT",
                amount: Number(cat.custom_amount) || 0,
                category_id: cat.id,
                sort_order: 0,
              });
            }
          } else {
            // Normal subcategory handling
            const originalCat = categories.find((c) => c.id === cat.id);
            const originalSubIds = new Set(
              originalCat?.subcategories.map((s) => s.id) || [],
            );
            const localSubIds = new Set(cat.subcategories.map((s) => s.id));

            const deletedSubIds = [...originalSubIds].filter(
              (id) => !localSubIds.has(id) && !id.startsWith("new-sub-"),
            );
            for (const subId of deletedSubIds) {
              await supabase
                .from("claims_subcategories")
                .delete()
                .eq("id", subId);
            }

            for (let j = 0; j < cat.subcategories.length; j++) {
              const sub = cat.subcategories[j];
              const isNewSub = sub.id.startsWith("new-sub-");

              if (isNewSub) {
                await supabase.from("claims_subcategories").insert({
                  organization_id: requireOrganizationId(),
                  name: sub.name,
                  amount: Number(sub.amount) || 0,
                  category_id: cat.id,
                  sort_order: j,
                });
              } else {
                await supabase
                  .from("claims_subcategories")
                  .update({
                    name: sub.name,
                    amount: Number(sub.amount) || 0,
                    sort_order: j,
                  })
                  .eq("id", sub.id);
              }
            }
          }
        }
      }

      // Save all target configurations
      await saveTargets();

      addToast({
        type: "success",
        title: "Categories Updated",
        message: "All changes have been saved",
      });

      onCategoriesUpdate();
      onClose();
    } catch (err) {
      logError("Error saving categories:", err);
      addToast({
        type: "error",
        title: "Save Failed",
        message: err instanceof Error ? err.message : "Database error occurred",
      });
    } finally {
      setSaving(false);
    }
  };

  const getCategoryTotal = (cat: CategoryWithSubs) => {
    if (cat.is_custom_amount_only) {
      return Number(cat.custom_amount) || 0;
    }
    return cat.subcategories.reduce(
      (sum, sub) => sum + (Number(sub.amount) || 0),
      0,
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      onConfirm={handleSave}
      title="Edit Categories & Subcategories"
      confirmText={saving ? "Saving..." : "Save Changes"}
      confirmDisabled={saving}
      size="xl"
    >
      <div
        ref={containerRef}
        style={{ minHeight: 300 }}
      >
        {/* Add Category Button */}
        <div
          style={{
            marginBottom: "var(--spacing-lg)",
            display: "flex",
            gap: "var(--spacing-md)",
          }}
        >
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={addCategory}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
          >
            <Plus size={14} />
            Add Category
          </button>

          <button
            type="button"
            className="btn btn-secondary btn-sm"
            onClick={autoSortAll}
            style={{ display: "flex", alignItems: "center", gap: 6 }}
            title="Sort: Fixed Top + Alphabetical"
          >
            <SortAsc size={14} />
            Auto Sort
          </button>
        </div>

        {/* Categories List */}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={localCategories.map((c) => c.id)}
            strategy={verticalListSortingStrategy}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--spacing-md)",
              }}
            >
              {localCategories.map((cat) => {
                return (
                <SortableCategoryItem key={cat.id} id={cat.id}>
                  {({
                    attributes,
                    listeners,
                  }: {
                    attributes: DraggableAttributes;
                    listeners: ReturnType<typeof useSortable>['listeners'];
                  }) => (
                    <>
                      {/* Category Header */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "var(--spacing-sm)",
                          padding: "12px 16px",
                          background: "var(--bg-secondary)",
                          borderBottom: "1px solid var(--border-color)",
                          borderRadius: "var(--border-radius-md) var(--border-radius-md) 0 0",
                        }}
                      >
                        {/* Drag Handle */}
                        <button
                          type="button"
                          {...attributes}
                          {...listeners}
                          style={{
                            background: "none",
                            border: "none",
                            cursor: "grab",
                            padding: 4,
                            display: "flex",
                            color: "var(--text-muted)",
                          }}
                          title="Drag to reorder"
                        >
                          <GripVertical size={16} />
                        </button>
                        <input
                          ref={(el) => {
                            if (el) categoryInputRefs.current.set(cat.id, el);
                          }}
                          type="text"
                          className="form-input"
                          placeholder="Category Name"
                          value={cat.name}
                          onChange={(e) =>
                            updateCategoryName(
                              cat.id,
                              e.target.value.toUpperCase(),
                            )
                          }
                          aria-label="Category name"
                          style={{
                            flex: 1,
                            fontWeight: 600,
                          }}
                        />

                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            fontSize: "var(--font-size-sm)",
                            cursor: "pointer",
                            whiteSpace: "nowrap",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={cat.is_custom_amount_only || false}
                            onChange={() => toggleCategoryMode(cat.id)}
                          />
                          Fixed Amount
                        </label>

                        {!cat.is_custom_amount_only && (
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() =>
                              setExpandedCategory(
                                expandedCategory === cat.id ? null : cat.id,
                              )
                            }
                            style={{ padding: "6px 10px" }}
                          >
                            <Edit2 size={14} />
                          </button>
                        )}

                        {/* Category Target Button */}
                        {(() => {
                          const catTarget = categoryTargets.get(cat.id);
                          const hasTarget = catTarget?.is_enabled;
                          const isUnsaved = cat.id.startsWith("new-");
                          return (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isUnsaved) {
                                  // Toggle "save first" inline message
                                  setExpandedCategoryTarget(
                                    expandedCategoryTarget === cat.id ? null : cat.id
                                  );
                                  return;
                                }
                                const existing = categoryTargets.get(cat.id);
                                if (!existing) {
                                  // No target yet → create (enabled) + open panel
                                  toggleCategoryTarget(cat.id, cat);
                                  setExpandedCategoryTarget(cat.id);
                                } else if (existing.is_enabled) {
                                  // Currently enabled → disable + close panel
                                  updateCategoryTarget(cat.id, "is_enabled", false);
                                  setExpandedCategoryTarget(null);
                                } else {
                                  // Currently disabled → enable + open panel
                                  updateCategoryTarget(cat.id, "is_enabled", true);
                                  setExpandedCategoryTarget(cat.id);
                                }
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                                padding: "5px 8px",
                                borderRadius: 6,
                                border: hasTarget
                                  ? "1.5px solid rgba(245, 158, 11, 0.5)"
                                  : "1.5px solid var(--border-color)",
                                background: hasTarget
                                  ? "rgba(245, 158, 11, 0.1)"
                                  : "transparent",
                                color: hasTarget
                                  ? "#d97706"
                                  : "var(--text-muted)",
                                fontSize: 12,
                                fontWeight: 600,
                                cursor: "pointer",
                                transition: "all 0.15s ease",
                                opacity: isUnsaved ? 0.6 : 1,
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.background = hasTarget
                                  ? "rgba(245, 158, 11, 0.2)"
                                  : "rgba(100, 116, 139, 0.08)";
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.background = hasTarget
                                  ? "rgba(245, 158, 11, 0.1)"
                                  : "transparent";
                              }}
                              title={isUnsaved ? "Save changes first to set targets" : "Configure target notification for this category"}
                              aria-label="Configure category target notification"
                            >
                              <Crosshair size={13} />
                            </button>
                          );
                        })()}

                        <button
                          type="button"
                          onClick={() => cloneCategory(cat)}
                          style={{
                            background: "rgba(30, 115, 190, 0.08)",
                            border: "1px solid rgba(30, 115, 190, 0.16)",
                            borderRadius: 8,
                            width: 36,
                            height: 36,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#1e73be",
                          }}
                          title="Clone category"
                          aria-label="Clone category"
                        >
                          <Copy size={15} />
                        </button>

                        <button
                          type="button"
                          onClick={() => deleteCategory(cat.id)}
                          style={{
                            background: "rgba(239, 68, 68, 0.08)",
                            border: "1px solid rgba(239, 68, 68, 0.18)",
                            borderRadius: 8,
                            width: 36,
                            height: 36,
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "var(--color-error)",
                          }}
                          title="Delete category"
                          aria-label="Delete category"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>

                      {/* Fixed Amount Input */}
                      {cat.is_custom_amount_only && (
                        <>
                        <div style={{ padding: "16px 20px" }}>
                          <label
                            style={{
                              display: "block",
                              fontSize: "var(--font-size-xs)",
                              fontWeight: 500,
                              color: "var(--text-muted)",
                              textTransform: "uppercase",
                              letterSpacing: "0.05em",
                              marginBottom: 6,
                            }}
                          >
                            Contract Amount
                          </label>
                          <div style={{ position: "relative", maxWidth: 240 }}>
                            <span
                              style={{
                                position: "absolute",
                                left: 10,
                                top: "50%",
                                transform: "translateY(-50%)",
                                color: "var(--text-muted)",
                                fontSize: "var(--font-size-sm)",
                                fontWeight: 500,
                                pointerEvents: "none",
                              }}
                            >
                              $
                            </span>
                            <input data-seq-private
                              type="number"
                              className="form-input"
                              placeholder="0.00"
                              value={cat.custom_amount || ""}
                              onChange={(e) =>
                                updateCategoryAmount(
                                  cat.id,
                                  parseFloat(e.target.value) || 0,
                                )
                              }
                              style={{
                                width: "100%",
                                paddingLeft: 22,
                                fontSize: "var(--font-size-md)",
                                fontWeight: 500,
                              }}
                            />
                          </div>
                        </div>
                        </>
                      )}

                      {/* Category Target Config Panel (all categories) */}
                      {expandedCategoryTarget === cat.id && (() => {
                        // Show "save first" message for unsaved categories
                        if (cat.id.startsWith("new-")) {
                          return (
                            <div
                              style={{
                                padding: "10px 20px",
                                background: "rgba(245, 158, 11, 0.06)",
                                borderBottom: "1px solid rgba(245, 158, 11, 0.12)",
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <Info size={14} style={{ color: "#d97706", flexShrink: 0 }} />
                              <span style={{ fontSize: 12, color: "#92400e", lineHeight: 1.4 }}>
                                Save your changes first, then come back to set up target reminders for this category.
                              </span>
                            </div>
                          );
                        }

                        const catTarget = categoryTargets.get(cat.id);
                        if (!catTarget) return null;
                        const isMultiSub = !cat.is_custom_amount_only;
                        const selectableSubs = cat.subcategories.filter(
                          (s) => s.name.toUpperCase() !== "FIXED AMOUNT" && !s.id.startsWith("new-sub-")
                        );

                        return (
                          <div
                            style={{
                              padding: "12px 20px 16px",
                              background: "rgba(245, 158, 11, 0.04)",
                              borderBottom: "1px solid rgba(245, 158, 11, 0.15)",
                            }}
                          >
                            {/* Header row */}
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                marginBottom: 10,
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "var(--font-size-sm)",
                                  fontWeight: 600,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  color: "#d97706",
                                }}
                              >
                                <Crosshair size={14} />
                                Category Target
                              </span>
                              <button
                                type="button"
                                onClick={() => toggleCategoryTarget(cat.id, cat)}
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  padding: 4,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 4,
                                  fontSize: "var(--font-size-xs)",
                                  color: catTarget.is_enabled
                                    ? "var(--color-success, #10b981)"
                                    : "var(--text-muted)",
                                }}
                              >
                                {catTarget.is_enabled ? (
                                  <><Bell size={12} /> Enabled</>
                                ) : (
                                  <><BellOff size={12} /> Disabled</>
                                )}
                              </button>
                            </div>

                            {/* How it works info: dynamic based on selections */}
                            {(() => {
                              const isPerc = catTarget.target_type === "percentage";
                              const val = catTarget.target_value;
                              const hasVal = val && val > 0;
                              const targetLabel = hasVal
                                ? isPerc ? `${val}%` : `$${val.toLocaleString()}`
                                : "the target you set";
                              const modeOnce = catTarget.trigger_mode === "once";

                              // Build the info sentence
                              let infoText: React.ReactNode;

                              if (isMultiSub) {
                                const allSelected = catTarget.included_subcategory_ids.length === selectableSubs.length;
                                const noneSelected = catTarget.included_subcategory_ids.length === 0;
                                const selectedNames = selectableSubs
                                  .filter((s) => catTarget.included_subcategory_ids.includes(s.id))
                                  .map((s) => s.name);

                                if (noneSelected) {
                                  infoText = (
                                    <>Select subcategories below to start tracking their combined {isPerc ? "percentage" : "amount"}.</>
                                  );
                                } else {
                                  const scope = allSelected
                                    ? <strong>all subcategories</strong>
                                    : <><strong>{selectedNames.join(", ")}</strong></>;
                                  infoText = (
                                    <>
                                      You'll be reminded {modeOnce ? <strong>once</strong> : <>
                                        <strong>each time</strong> it updates</>} when the combined{" "}
                                      {isPerc ? "percentage" : "amount"} of {scope} reaches{" "}
                                      <strong>{targetLabel}</strong>.
                                    </>
                                  );
                                }
                              } else {
                                // Fixed category
                                infoText = (
                                  <>
                                    You'll be reminded {modeOnce ? <strong>once</strong> : <>
                                      <strong>each time</strong> it updates</>} when the claimed{" "}
                                    {isPerc ? "percentage" : "amount"} for this category reaches{" "}
                                    <strong>{targetLabel}</strong>.
                                  </>
                                );
                              }

                              return (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 7,
                                    padding: "8px 10px",
                                    marginBottom: 10,
                                    background: "rgba(30, 115, 190, 0.06)",
                                    borderRadius: 6,
                                    border: "1px solid rgba(30, 115, 190, 0.12)",
                                  }}
                                >
                                  <Info size={13} style={{ color: "#1e73be", flexShrink: 0, marginTop: 1 }} />
                                  <span style={{ fontSize: 11, color: "#1e5a94", lineHeight: 1.5 }}>
                                    {infoText}
                                  </span>
                                </div>
                              );
                            })()}

                            {/* Target Type + Value row */}
                            <div
                              style={{
                                display: "flex",
                                gap: 8,
                                marginBottom: 8,
                                alignItems: "center",
                                flexWrap: "wrap",
                              }}
                            >
                              <span style={{ fontSize: "var(--font-size-sm)", whiteSpace: "nowrap" }}>
                                Trigger when{isMultiSub ? " overall" : ""}:
                              </span>
                              <select
                                className="form-input-sm select-auto"
                                value={catTarget.target_type}
                                onChange={(e) => updateCategoryTarget(cat.id, "target_type", e.target.value)}
                              >
                                <option value="percentage">Percentage ≥</option>
                                <option value="amount">Amount ≥</option>
                              </select>
                              <input data-seq-private
                                type="number"
                                className="form-input"
                                value={catTarget.target_value || ""}
                                onChange={(e) => updateCategoryTarget(cat.id, "target_value", parseFloat(e.target.value) || 0)}
                                placeholder={catTarget.target_type === "amount" ? "Amount" : "80"}
                                style={{ width: 90, padding: "4px 8px", fontSize: "var(--font-size-sm)" }}
                              />
                              <span style={{ fontSize: "var(--font-size-sm)", color: "var(--text-muted)" }}>
                                {catTarget.target_type === "amount" ? "$" : "%"}
                              </span>
                            </div>

                            {/* Trigger Mode */}
                            <div style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
                              <span style={{ fontSize: "var(--font-size-sm)", whiteSpace: "nowrap" }}>
                                Mode:
                              </span>
                              <select
                                className="form-input-sm select-auto"
                                value={catTarget.trigger_mode}
                                onChange={(e) => updateCategoryTarget(cat.id, "trigger_mode", e.target.value)}
                              >
                                <option value="once">Once only</option>
                                <option value="every_change">Every change past target</option>
                              </select>
                            </div>

                            {/* Subcategory Inclusion Selector (only for multi-subcategory categories) */}
                            {isMultiSub && selectableSubs.length > 0 && (
                              <div
                                style={{
                                  marginTop: 4,
                                  marginBottom: 10,
                                  padding: "10px 12px",
                                  background: "rgba(255, 255, 255, 0.7)",
                                  border: "1px solid var(--border-color)",
                                  borderRadius: 8,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    marginBottom: 8,
                                  }}
                                >
                                  <span
                                    style={{
                                      fontSize: "var(--font-size-xs)",
                                      fontWeight: 600,
                                      color: "var(--text-secondary)",
                                      textTransform: "uppercase",
                                      letterSpacing: "0.04em",
                                    }}
                                  >
                                    Include in calculation ({catTarget.included_subcategory_ids.length}/{selectableSubs.length})
                                  </span>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const allSelected = catTarget.included_subcategory_ids.length === selectableSubs.length;
                                      toggleAllSubcategories(cat.id, cat, !allSelected);
                                    }}
                                    style={{
                                      background: "none",
                                      border: "none",
                                      cursor: "pointer",
                                      fontSize: 11,
                                      color: "#1e73be",
                                      fontWeight: 600,
                                      padding: "2px 6px",
                                    }}
                                  >
                                    {catTarget.included_subcategory_ids.length === selectableSubs.length
                                      ? "Deselect All"
                                      : "Select All"}
                                  </button>
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    flexWrap: "wrap",
                                    gap: 6,
                                  }}
                                >
                                  {selectableSubs.map((sub) => {
                                    const isIncluded = catTarget.included_subcategory_ids.includes(sub.id);
                                    return (
                                      <label
                                        key={sub.id}
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 5,
                                          padding: "4px 10px",
                                          borderRadius: 6,
                                          border: isIncluded
                                            ? "1px solid rgba(30, 115, 190, 0.4)"
                                            : "1px solid var(--border-color)",
                                          background: isIncluded
                                            ? "rgba(30, 115, 190, 0.08)"
                                            : "transparent",
                                          cursor: "pointer",
                                          fontSize: 12,
                                          fontWeight: 500,
                                          transition: "all 0.15s ease",
                                          color: isIncluded ? "#1e73be" : "var(--text-secondary)",
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          checked={isIncluded}
                                          onChange={() => toggleSubcategoryInclusion(cat.id, sub.id)}
                                          style={{ margin: 0, accentColor: "#1e73be" }}
                                        />
                                        {sub.name}
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            )}

                            {/* Custom Message */}
                            <div>
                              <textarea
                                className="form-input"
                                placeholder="Custom notification message (optional)..."
                                value={catTarget.message}
                                onChange={(e) => updateCategoryTarget(cat.id, "message", e.target.value)}
                                rows={2}
                                style={{
                                  width: "100%",
                                  resize: "vertical",
                                  fontSize: "var(--font-size-sm)",
                                  padding: "6px 8px",
                                }}
                              />
                            </div>
                          </div>
                        );
                      })()}

                      {/* Subcategories */}
                      {!cat.is_custom_amount_only &&
                        expandedCategory === cat.id && (
                          <div
                            style={{
                              padding: "16px 20px",
                              background: "var(--bg-primary)",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "var(--spacing-sm)",
                              }}
                            >
                              <span
                                style={{
                                  fontSize: "var(--font-size-sm)",
                                  fontWeight: 500,
                                  color: "var(--text-muted)",
                                }}
                              >
                                Subcategories
                              </span>

                              {/* Add Subcategory Dropdown */}
                              <div style={{ position: "relative" }}>
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  onClick={() =>
                                    setShowSubDropdown(
                                      showSubDropdown === cat.id
                                        ? null
                                        : cat.id,
                                    )
                                  }
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                    padding: "4px 8px",
                                  }}
                                >
                                  <Plus size={12} />
                                  Add
                                  <ChevronDown size={12} />
                                </button>

                                {showSubDropdown === cat.id && (
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "100%",
                                      right: 0,
                                      marginTop: 4,
                                      background: "var(--bg-primary)",
                                      border: "1px solid var(--border-color)",
                                      borderRadius: "var(--border-radius-md)",
                                      boxShadow: "var(--shadow-lg)",
                                      minWidth: 180,
                                      maxHeight: 250,
                                      overflowY: "auto",
                                      zIndex: 100,
                                    }}
                                  >
                                    {/* Custom option */}
                                    <button
                                      type="button"
                                      onClick={() => addSubcategory(cat.id)}
                                      style={{
                                        width: "100%",
                                        padding: "8px 12px",
                                        border: "none",
                                        background: "none",
                                        textAlign: "left",
                                        cursor: "pointer",
                                        fontWeight: 500,
                                        borderBottom:
                                          "1px solid var(--border-color)",
                                      }}
                                      onMouseOver={(e) =>
                                        (e.currentTarget.style.background =
                                          "var(--bg-secondary)")
                                      }
                                      onMouseOut={(e) =>
                                        (e.currentTarget.style.background =
                                          "none")
                                      }
                                    >
                                      + Custom Name...
                                    </button>

                                    {/* Predefined options */}
                                    {getAvailableSubcategories(cat).length >
                                    0 ? (
                                      getAvailableSubcategories(cat).map(
                                        (name) => (
                                          <button
                                            key={name}
                                            type="button"
                                            onClick={() =>
                                              addSubcategory(cat.id, name)
                                            }
                                            style={{
                                              width: "100%",
                                              padding: "8px 12px",
                                              border: "none",
                                              background: "none",
                                              textAlign: "left",
                                              cursor: "pointer",
                                            }}
                                            onMouseOver={(e) =>
                                              (e.currentTarget.style.background =
                                                "var(--bg-secondary)")
                                            }
                                            onMouseOut={(e) =>
                                              (e.currentTarget.style.background =
                                                "none")
                                            }
                                          >
                                            {name}
                                          </button>
                                        ),
                                      )
                                    ) : (
                                      <div
                                        style={{
                                          padding: "8px 12px",
                                          color: "var(--text-muted)",
                                          fontSize: "var(--font-size-sm)",
                                        }}
                                      >
                                        All predefined levels added
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>

                            {cat.subcategories.length === 0 ? (
                              <p
                                style={{
                                  color: "var(--text-muted)",
                                  fontSize: "var(--font-size-sm)",
                                  fontStyle: "italic",
                                }}
                              >
                                No subcategories. Click "Add" to create one.
                              </p>
                            ) : (
                              <SortableContext
                                items={cat.subcategories.map((s) => s.id)}
                                strategy={verticalListSortingStrategy}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 8,
                                  }}
                                >
                                  {cat.subcategories.map((sub) => (
                                    <SortableSubItem key={sub.id} id={sub.id}>
                                      {({
                                        attributes,
                                        listeners,
                                      }: {
                                        attributes: DraggableAttributes;
                                        listeners: ReturnType<typeof useSortable>['listeners'];
                                       }) => (
                                        <div>
                                        <div
                                          style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            padding: "8px 10px",
                                            background: "var(--bg-secondary)",
                                            borderRadius: "var(--border-radius-md)",
                                            border: "1px solid var(--border-color)",
                                            transition: "box-shadow 0.15s ease",
                                          }}
                                          onMouseOver={(e) => (e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.06)")}
                                          onMouseOut={(e) => (e.currentTarget.style.boxShadow = "none")}
                                        >
                                          {/* Drag Handle */}
                                          <button
                                            type="button"
                                            {...attributes}
                                            {...listeners}
                                            style={{
                                              background: "none",
                                              border: "none",
                                              cursor: "grab",
                                              padding: 4,
                                              display: "flex",
                                              color: "var(--text-muted)",
                                              flexShrink: 0,
                                            }}
                                            title="Drag to reorder"
                                          >
                                            <GripVertical size={14} />
                                          </button>
                                          <input
                                            type="text"
                                            className="form-input"
                                            placeholder="Subcategory Name"
                                            value={sub.name}
                                            onChange={(e) =>
                                              updateSubcategory(
                                                cat.id,
                                                sub.id,
                                                "name",
                                                e.target.value,
                                              )
                                            }
                                            aria-label="Subcategory name"
                                            style={{
                                              flex: 1,
                                              minWidth: 0,
                                            }}
                                          />
                                          {targets.get(sub.id)?.is_enabled && (() => {
                                            const target = targets.get(sub.id)!;
                                            const isHovered = hoveredTargetBadge?.id === sub.id;
                                            return (
                                              <span
                                                style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}
                                                onMouseEnter={(e) => {
                                                  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                                                  setHoveredTargetBadge({ id: sub.id, rect });
                                                }}
                                                onMouseLeave={() => setHoveredTargetBadge(null)}
                                              >
                                                <span
                                                  style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: 3,
                                                    padding: "2px 7px",
                                                    borderRadius: 10,
                                                    background: isHovered ? "rgba(245, 158, 11, 0.22)" : "rgba(245, 158, 11, 0.12)",
                                                    color: "#d97706",
                                                    fontSize: 9,
                                                    fontWeight: 700,
                                                    letterSpacing: "0.5px",
                                                    textTransform: "uppercase" as const,
                                                    whiteSpace: "nowrap" as const,
                                                    border: "1px solid rgba(245, 158, 11, 0.25)",
                                                    cursor: "default",
                                                    transition: "background 0.15s ease",
                                                  }}
                                                >
                                                  <Crosshair size={9} />
                                                  Target
                                                </span>
                                                {/* Rich tooltip via portal */}
                                                {isHovered && hoveredTargetBadge && createPortal(
                                                  <div
                                                    style={{
                                                      position: "fixed",
                                                      top: hoveredTargetBadge.rect.bottom + 6,
                                                      left: hoveredTargetBadge.rect.left,
                                                      zIndex: 99999,
                                                      minWidth: 240,
                                                      maxWidth: 320,
                                                      background: "#1e293b",
                                                      color: "#f1f5f9",
                                                      borderRadius: 10,
                                                      padding: "12px 14px",
                                                      boxShadow: "0 8px 24px rgba(0,0,0,0.25), 0 2px 8px rgba(0,0,0,0.15)",
                                                      fontSize: 11,
                                                      lineHeight: 1.5,
                                                      animation: "fadeIn 0.15s ease",
                                                      pointerEvents: "none",
                                                    }}
                                                  >
                                                    {/* Arrow */}
                                                    <div style={{
                                                      position: "absolute",
                                                      top: -5,
                                                      left: 12,
                                                      width: 10,
                                                      height: 10,
                                                      background: "#1e293b",
                                                      transform: "rotate(45deg)",
                                                      borderRadius: 2,
                                                    }} />
                                                    {/* Header */}
                                                    <div style={{
                                                      display: "flex",
                                                      alignItems: "center",
                                                      gap: 5,
                                                      marginBottom: 8,
                                                      paddingBottom: 7,
                                                      borderBottom: "1px solid rgba(148, 163, 184, 0.2)",
                                                    }}>
                                                      <Crosshair size={12} style={{ color: "#fbbf24" }} />
                                                      <span style={{ fontWeight: 700, fontSize: 12, color: "#fbbf24" }}>Target Notification</span>
                                                    </div>
                                                    {/* Details */}
                                                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                                                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                                                        <span style={{ color: "#94a3b8" }}>Type</span>
                                                        <span style={{ fontWeight: 600, textTransform: "capitalize" }}>
                                                          {target.target_type}
                                                        </span>
                                                      </div>
                                                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                                                        <span style={{ color: "#94a3b8" }}>Threshold</span>
                                                        <span style={{ fontWeight: 600, color: "#fbbf24" }}>
                                                          {target.target_type === "percentage"
                                                            ? `${target.target_value}%`
                                                            : `$${target.target_value.toLocaleString()}`}
                                                        </span>
                                                      </div>
                                                      <div style={{ display: "flex", justifyContent: "space-between" }}>
                                                        <span style={{ color: "#94a3b8" }}>Trigger</span>
                                                        <span style={{
                                                          fontWeight: 600,
                                                          padding: "1px 6px",
                                                          borderRadius: 6,
                                                          fontSize: 10,
                                                          background: target.trigger_mode === "once" ? "rgba(59, 130, 246, 0.2)" : "rgba(168, 85, 247, 0.2)",
                                                          color: target.trigger_mode === "once" ? "#93c5fd" : "#c4b5fd",
                                                        }}>
                                                          {target.trigger_mode === "once" ? "Once only" : "Every time"}
                                                        </span>
                                                      </div>
                                                    </div>
                                                    {/* Message */}
                                                    {target.message && (
                                                      <div style={{
                                                        marginTop: 8,
                                                        paddingTop: 7,
                                                        borderTop: "1px solid rgba(148, 163, 184, 0.2)",
                                                      }}>
                                                        <div style={{ color: "#94a3b8", fontSize: 10, marginBottom: 3 }}>Message</div>
                                                        <div style={{
                                                          fontSize: 11,
                                                          color: "#e2e8f0",
                                                          fontStyle: "italic",
                                                          lineHeight: 1.4,
                                                          wordBreak: "break-word",
                                                        }}>
                                                          "{target.message}"
                                                        </div>
                                                      </div>
                                                    )}
                                                  </div>,
                                                  document.body
                                                )}
                                              </span>
                                            );
                                          })()}
                                          <div
                                            style={{
                                              position: "relative",
                                              flexShrink: 0,
                                              width: 140,
                                            }}
                                          >
                                            <span
                                              style={{
                                                position: "absolute",
                                                left: 8,
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                color: "var(--text-muted)",
                                                fontSize: "var(--font-size-xs)",
                                                pointerEvents: "none",
                                              }}
                                            >
                                              $
                                            </span>
                                            <input data-seq-private
                                              ref={(el) => {
                                                if (el)
                                                  subcategoryInputRefs.current.set(
                                                    sub.id,
                                                    el,
                                                  );
                                              }}
                                              type="number"
                                              className="form-input"
                                              placeholder="0.00"
                                              value={sub.amount || ""}
                                              onChange={(e) =>
                                                updateSubcategory(
                                                  cat.id,
                                                  sub.id,
                                                  "amount",
                                                  parseFloat(e.target.value) || 0,
                                                )
                                              }
                                              style={{
                                                width: "100%",
                                                paddingLeft: 20,
                                                fontVariantNumeric: "tabular-nums",
                                              }}
                                            />
                                          </div>
                                          {/* Action buttons group */}
                                          <div
                                            style={{
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 4,
                                              flexShrink: 0,
                                            }}
                                          >
                                            {/* Target button */}
                                            <button
                                              type="button"
                                              onClick={() => {
                                                const isSubUnsaved = sub.id.startsWith("new-sub-");
                                                if (isSubUnsaved) {
                                                  setExpandedTarget(
                                                    expandedTarget === sub.id ? null : sub.id
                                                  );
                                                  return;
                                                }
                                                const existing = targets.get(sub.id);
                                                if (!existing) {
                                                  // No target yet → create (enabled) + open panel
                                                  toggleTarget(sub.id);
                                                  setExpandedTarget(sub.id);
                                                } else if (existing.is_enabled) {
                                                  // Currently enabled → disable + close panel
                                                  updateTarget(sub.id, "is_enabled", false);
                                                  setExpandedTarget(null);
                                                } else {
                                                  // Currently disabled → enable + open panel
                                                  updateTarget(sub.id, "is_enabled", true);
                                                  setExpandedTarget(sub.id);
                                                }
                                              }}
                                              style={{
                                                background: targets.get(sub.id)?.is_enabled
                                                  ? "rgba(245, 158, 11, 0.15)"
                                                  : "transparent",
                                                border: targets.get(sub.id)?.is_enabled
                                                  ? "1.5px solid rgba(245, 158, 11, 0.5)"
                                                  : "1.5px solid var(--border-color)",
                                                borderRadius: 6,
                                                padding: 5,
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: targets.get(sub.id)?.is_enabled
                                                  ? "var(--color-warning, #f59e0b)"
                                                  : "var(--text-secondary, #64748b)",
                                                transition: "all 0.15s ease",
                                                outline: expandedTarget === sub.id
                                                  ? "2px solid rgba(245, 158, 11, 0.4)"
                                                  : "none",
                                                outlineOffset: 1,
                                                opacity: sub.id.startsWith("new-sub-") ? 0.6 : 1,
                                              }}
                                              title={sub.id.startsWith("new-sub-") ? "Save changes first to set targets" : "Configure target notification"}
                                              aria-label="Configure subcategory target notification"
                                              onMouseOver={(e) => {
                                                e.currentTarget.style.background = targets.get(sub.id)?.is_enabled
                                                  ? "rgba(245, 158, 11, 0.25)"
                                                  : "rgba(100, 116, 139, 0.12)";
                                              }}
                                              onMouseOut={(e) => {
                                                e.currentTarget.style.background = targets.get(sub.id)?.is_enabled
                                                  ? "rgba(245, 158, 11, 0.15)"
                                                  : "transparent";
                                              }}
                                            >
                                              <Crosshair size={14} />
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => cloneSubcategory(cat.id, sub)}
                                              style={{
                                                background: "rgba(30, 115, 190, 0.08)",
                                                border: "1px solid rgba(30, 115, 190, 0.16)",
                                                borderRadius: 8,
                                                width: 34,
                                                height: 34,
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "#1e73be",
                                              }}
                                              title="Clone subcategory"
                                              aria-label="Clone subcategory"
                                            >
                                              <Copy size={14} />
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() =>
                                                deleteSubcategory(cat.id, sub.id)
                                              }
                                              style={{
                                                background: "rgba(239, 68, 68, 0.08)",
                                                border: "1px solid rgba(239, 68, 68, 0.18)",
                                                borderRadius: 8,
                                                width: 34,
                                                height: 34,
                                                cursor: "pointer",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "var(--color-error)",
                                              }}
                                              title="Delete subcategory"
                                              aria-label="Delete subcategory"
                                            >
                                              <Trash2 size={14} />
                                            </button>
                                          </div>
                                        </div>

                                        {/* Target Config Panel */}
                                        {expandedTarget === sub.id && (
                                          <div
                                            style={{
                                              marginTop: 8,
                                              padding: "12px 16px",
                                              background: "rgba(245, 158, 11, 0.05)",
                                              border: "1px solid rgba(245, 158, 11, 0.2)",
                                              borderRadius: 8,
                                            }}
                                          >
                                            {/* Save first message for unsaved subcategories */}
                                            {sub.id.startsWith("new-sub-") ? (
                                              <div
                                                style={{
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 8,
                                                }}
                                              >
                                                <Info size={14} style={{ color: "#d97706", flexShrink: 0 }} />
                                                <span style={{ fontSize: 12, color: "#92400e", lineHeight: 1.4 }}>
                                                  Save your changes first, then come back to set up target reminders for this item.
                                                </span>
                                              </div>
                                            ) : (
                                              <>
                                            <div
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                marginBottom: 10,
                                              }}
                                            >
                                              <span
                                                style={{
                                                  fontSize: "var(--font-size-sm)",
                                                  fontWeight: 600,
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 6,
                                                }}
                                              >
                                                <Crosshair size={14} />
                                                Target Notification
                                              </span>
                                              <button
                                                type="button"
                                                onClick={() => toggleTarget(sub.id)}
                                                style={{
                                                  background: "none",
                                                  border: "none",
                                                  cursor: "pointer",
                                                  padding: 4,
                                                  display: "flex",
                                                  alignItems: "center",
                                                  gap: 4,
                                                  fontSize: "var(--font-size-xs)",
                                                  color: targets.get(sub.id)?.is_enabled
                                                    ? "var(--color-success, #10b981)"
                                                    : "var(--text-muted)",
                                                }}
                                              >
                                                {targets.get(sub.id)?.is_enabled ? (
                                                  <><Bell size={12} /> Enabled</>
                                                ) : (
                                                  <><BellOff size={12} /> Disabled</>
                                                )}
                                              </button>
                                            </div>

                                            {/* How it works info: dynamic based on selections */}
                                            {(() => {
                                              const subTarget = targets.get(sub.id);
                                              const isPerc = subTarget?.target_type === "percentage" || !subTarget?.target_type;
                                              const val = subTarget?.target_value;
                                              const hasVal = val && val > 0;
                                              const targetLabel = hasVal
                                                ? isPerc ? `${val}%` : `$${val.toLocaleString()}`
                                                : "the target you set";
                                              const modeOnce = !subTarget?.trigger_mode || subTarget.trigger_mode === "once";

                                              return (
                                                <div
                                                  style={{
                                                    display: "flex",
                                                    alignItems: "flex-start",
                                                    gap: 7,
                                                    padding: "8px 10px",
                                                    marginBottom: 10,
                                                    background: "rgba(30, 115, 190, 0.06)",
                                                    borderRadius: 6,
                                                    border: "1px solid rgba(30, 115, 190, 0.12)",
                                                  }}
                                                >
                                                  <Info size={13} style={{ color: "#1e73be", flexShrink: 0, marginTop: 1 }} />
                                                  <span style={{ fontSize: 11, color: "#1e5a94", lineHeight: 1.5 }}>
                                                    You'll be reminded {modeOnce ? <strong>once</strong> : <><strong>each time</strong> it updates</>}
                                                    {" "}when this item's {isPerc ? "percentage" : "claimed amount"} reaches{" "}
                                                    <strong>{targetLabel}</strong>.
                                                  </span>
                                                </div>
                                              );
                                            })()}

                                            {/* Target Type + Value */}
                                            <div
                                              style={{
                                                display: "flex",
                                                gap: 8,
                                                marginBottom: 8,
                                                alignItems: "center",
                                                flexWrap: "wrap",
                                              }}
                                            >
                                              <span
                                                style={{
                                                  fontSize: "var(--font-size-sm)",
                                                  whiteSpace: "nowrap",
                                                }}
                                              >
                                                Trigger when:
                                              </span>
                                              <select
                                                className="form-input-sm select-auto"
                                                value={
                                                  targets.get(sub.id)?.target_type || "percentage"
                                                }
                                                onChange={(e) =>
                                                  updateTarget(
                                                    sub.id,
                                                    "target_type",
                                                    e.target.value
                                                  )
                                                }
                                              >
                                                <option value="percentage">
                                                  Percentage ≥
                                                </option>
                                                <option value="amount">
                                                  Amount ≥
                                                </option>
                                              </select>
                                              <input data-seq-private
                                                type="number"
                                                className="form-input"
                                                value={
                                                  targets.get(sub.id)?.target_value || ""
                                                }
                                                onChange={(e) =>
                                                  updateTarget(
                                                    sub.id,
                                                    "target_value",
                                                    parseFloat(e.target.value) || 0
                                                  )
                                                }
                                                placeholder={
                                                  targets.get(sub.id)?.target_type === "amount"
                                                    ? "Amount"
                                                    : "75"
                                                }
                                                style={{
                                                  width: 90,
                                                  padding: "4px 8px",
                                                  fontSize: "var(--font-size-sm)",
                                                }}
                                              />
                                              <span
                                                style={{
                                                  fontSize: "var(--font-size-sm)",
                                                  color: "var(--text-muted)",
                                                }}
                                              >
                                                {targets.get(sub.id)?.target_type === "amount"
                                                  ? "$"
                                                  : "%"}
                                              </span>
                                            </div>

                                            {/* Trigger Mode */}
                                            <div
                                              style={{
                                                display: "flex",
                                                gap: 8,
                                                marginBottom: 8,
                                                alignItems: "center",
                                              }}
                                            >
                                              <span
                                                style={{
                                                  fontSize: "var(--font-size-sm)",
                                                  whiteSpace: "nowrap",
                                                }}
                                              >
                                                Mode:
                                              </span>
                                              <select
                                                className="form-input-sm select-auto"
                                                value={
                                                  targets.get(sub.id)?.trigger_mode || "once"
                                                }
                                                onChange={(e) =>
                                                  updateTarget(
                                                    sub.id,
                                                    "trigger_mode",
                                                    e.target.value
                                                  )
                                                }
                                              >
                                                <option value="once">
                                                  Once only
                                                </option>
                                                <option value="every_change">
                                                  Every change past target
                                                </option>
                                              </select>
                                            </div>

                                            {/* Custom Message */}
                                            <div>
                                              <textarea
                                                className="form-input"
                                                placeholder="Custom notification message (optional)..."
                                                value={
                                                  targets.get(sub.id)?.message || ""
                                                }
                                                onChange={(e) =>
                                                  updateTarget(
                                                    sub.id,
                                                    "message",
                                                    e.target.value
                                                  )
                                                }
                                                rows={2}
                                                style={{
                                                  width: "100%",
                                                  resize: "vertical",
                                                  fontSize: "var(--font-size-sm)",
                                                  padding: "6px 8px",
                                                }}
                                              />
                                            </div>
                                              </>
                                            )}
                                          </div>
                                        )}
                                        </div>
                                      )}
                                    </SortableSubItem>
                                  ))}
                                </div>
                              </SortableContext>
                            )}

                            {/* Subcategory Total */}
                            <div data-seq-private=""
                              style={{
                                marginTop: "var(--spacing-sm)",
                                paddingTop: "var(--spacing-sm)",
                                borderTop: "1px solid var(--border-color)",
                                display: "flex",
                                justifyContent: "flex-end",
                                fontSize: "var(--font-size-sm)",
                                fontWeight: 500,
                              }}
                            >
                              Total: $
                              {getCategoryTotal(cat).toLocaleString("en-AU", {
                                minimumFractionDigits: 2,
                              })}
                            </div>
                          </div>
                        )}

                      {/* Collapsed subcategory summary */}
                      {!cat.is_custom_amount_only &&
                        expandedCategory !== cat.id && (
                          <div
                            style={{
                              padding: "10px 20px",
                              fontSize: "var(--font-size-sm)",
                              color: "var(--text-muted)",
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              cursor: "pointer",
                              borderTop: "1px solid var(--border-color)",
                              transition: "background 0.15s ease",
                            }}
                            onClick={() => setExpandedCategory(cat.id)}
                            onMouseOver={(e) => (e.currentTarget.style.background = "var(--bg-secondary)")}
                            onMouseOut={(e) => (e.currentTarget.style.background = "transparent")}
                          >
                            <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              <ChevronDown size={14} />
                              {cat.subcategories.length} subcategor
                              {cat.subcategories.length === 1 ? "y" : "ies"}
                            </span>
                            <span data-seq-private="" style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                              $
                              {getCategoryTotal(cat).toLocaleString("en-AU", {
                                minimumFractionDigits: 2,
                              })}
                            </span>
                          </div>
                        )}
                    </>
                  )}
                </SortableCategoryItem>
                );
              })}
            </div>
          </SortableContext>
        </DndContext>

        {localCategories.length === 0 && (
          <p
            style={{
              textAlign: "center",
              color: "var(--text-muted)",
              padding: "var(--spacing-xl)",
            }}
          >
            No categories. Click "Add Category" to create one.
          </p>
        )}
      </div>
    </Modal>
  );
}
